import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPatch } from '../api/client.js';
import { EmailRecipients, toRecipientObjects } from './EmailRecipients.jsx';

/**
 * NotificationEmails — standalone Overview-tab control for who receives a
 * project's approval & digest emails.
 *
 * Lifted out of the Brand-brief edit form (ADR-0027): adding a recipient used to
 * mean clicking "Edit" on the whole brief, scrolling past nine fields, and
 * re-saving positioning/audience/voice. Now the add control is always visible
 * and persists with a partial `PATCH /projects/:key { recipient_emails }` — no
 * full-brief round-trip, no unrelated re-validation.
 *
 * Local edits are explicit-save (matches the brief's pattern): the Save button
 * enables only when the list differs from the persisted value, and a refetch
 * never clobbers in-progress typing. The mutation reads a ref so an address that
 * was typed but committed on blur (rather than Enter) is still included when the
 * owner clicks Save in the same tick.
 */
export function NotificationEmails({ projectKey, project }) {
  const qc = useQueryClient();
  const saved = useMemo(() => toRecipientObjects(project.recipient_emails), [project.recipient_emails]);
  const savedKey = JSON.stringify(saved);

  const [list, setList] = useState(saved);
  const listRef = useRef(saved);
  const applyList = (next) => { listRef.current = next; setList(next); };

  const [msg, setMsg] = useState(null);
  const dirty = JSON.stringify(list) !== savedKey;

  // Follow server-side changes (e.g. our own successful save) only when there
  // are no unsaved local edits, so a refetch never overwrites in-progress work.
  useEffect(() => {
    if (!dirty) applyList(saved);
    // Re-seed only when the persisted value changes; `dirty`/`saved` are read
    // from the render in which savedKey changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  const saveM = useMutation({
    mutationFn: () => apiPatch(`/projects/${encodeURIComponent(projectKey)}`, { recipient_emails: listRef.current }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', projectKey] });
      setMsg({ kind: 'ok', text: 'Notification emails saved.' });
      setTimeout(() => setMsg(null), 4000);
    },
  });

  const active = list.filter((r) => r.enabled).length;
  const paused = list.length - active;
  const summary = list.length === 0
    ? 'none yet'
    : `${active} active${paused ? ` · ${paused} paused` : ''}`;

  return (
    <section className="post-section notif-emails">
      <div className="section-h-row">
        <h2>Notification emails</h2>
        <span className="muted small">{summary}</span>
      </div>
      <p className="muted small notif-lead">
        Add the people who should get an email when a post needs approval, plus the periodic digest. Pause anyone
        without removing them — paused addresses keep their place but stop receiving mail.
      </p>

      {msg && <div className={`post-action-msg ${msg.kind}`}>{msg.text}</div>}

      <EmailRecipients value={list} onChange={applyList} />

      {saveM.isError && (
        <div className="post-action-msg err">
          {saveM.error?.body?.detail
            ?? saveM.error?.body?.issues?.[0]?.message
            ?? saveM.error?.body?.error
            ?? saveM.error?.message
            ?? 'Save failed — please try again.'}
        </div>
      )}

      <div className="form-actions notif-actions">
        {dirty && !saveM.isPending && (
          <span className="notif-dirty" role="status">Unsaved changes</span>
        )}
        {dirty && (
          <button type="button" className="btn-outline" onClick={() => applyList(saved)} disabled={saveM.isPending}>
            Reset
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => saveM.mutate()}
          disabled={!dirty || saveM.isPending}
        >
          {saveM.isPending ? 'Saving…' : 'Save emails'}
        </button>
      </div>
    </section>
  );
}
